import { useEffect, useState } from "react";
import { Banknote, ExternalLink, Link as LinkIcon, Shield, X, type LucideIcon } from "lucide-react";
import {
  getAffiliate,
  trackAffiliateClick,
  type AffiliateVariant,
} from "@/config/affiliates";

interface Props {
  variant: AffiliateVariant;
  placement: string;
  compact?: boolean;
  /** Si défini, autorise la fermeture (mémorisée en sessionStorage). */
  dismissibleId?: string;
  /** Optionnel : surcharge le titre par défaut. */
  titleOverride?: string;
}

const CONTENT: Record<
  AffiliateVariant,
  {
    Icon: LucideIcon;
    title: string;
    subtitle: string;
    bullets: string[];
    cta: string;
    aria: string;
  }
> = {
  credit: {
    Icon: Banknote,
    title: "Obtenez le meilleur taux pour votre LMNP",
    subtitle: "Simulation gratuite en 2 min",
    bullets: [
      "Comparaison de 150+ banques",
      "Courtier gratuit, payé par la banque",
      "Taux négociés jusqu'à -0.4%",
    ],
    cta: "Comparer les taux",
    aria:
      "Comparer les taux de crédit immobilier via notre partenaire Meilleurtaux (nouvel onglet)",
  },
  assurance: {
    Icon: Shield,
    title: "Économisez sur votre assurance emprunteur",
    subtitle: "Jusqu'à 15 000€ d'économies sur la durée du prêt",
    bullets: [
      "Jusqu'à -70% vs assurance banque",
      "Changement possible à tout moment (loi Lemoine)",
      "Garanties équivalentes ou supérieures",
    ],
    cta: "Simuler mon assurance",
    aria:
      "Simuler une assurance de prêt via notre partenaire Meilleurtaux (nouvel onglet)",
  },
};

const SS_PREFIX = "rentalmnp:affiliate:dismissed:";

export function AffiliateCard({
  variant,
  placement,
  compact = false,
  dismissibleId,
  titleOverride,
}: Props) {
  const aff = getAffiliate(variant);
  const c = CONTENT[variant];
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dismissibleId || typeof window === "undefined") return;
    if (window.sessionStorage.getItem(SS_PREFIX + dismissibleId) === "1") {
      setDismissed(true);
    }
  }, [dismissibleId]);

  if (dismissed) return null;

  const handleClick = () => {
    trackAffiliateClick(aff.partner, aff.productName, placement);
  };

  const handleDismiss = () => {
    if (!dismissibleId) return;
    try {
      window.sessionStorage.setItem(SS_PREFIX + dismissibleId, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  if (compact) {
    return (
      <div className="relative flex flex-wrap items-center gap-3 rounded-xl border border-[#BFDBFE] bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary" aria-hidden>
          <c.Icon className="h-5 w-5" />
        </span>
        <p className="flex-1 min-w-[140px] text-sm font-semibold text-foreground">
          {titleOverride ?? c.title}
        </p>
        <a
          href={aff.url}
          onClick={handleClick}
          target="_blank"
          rel="noopener noreferrer sponsored"
          aria-label={c.aria}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-dark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {c.cta}
          <ExternalLink className="h-3 w-3" />
        </a>
        <p className="flex basis-full items-center gap-1 text-[11px] text-muted-foreground">
          <LinkIcon className="h-3 w-3" aria-hidden />
          Lien partenaire
        </p>
        {dismissibleId && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fermer cette suggestion"
            className="absolute right-1.5 top-1.5 rounded p-0.5 text-muted-foreground hover:bg-background/60 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="relative space-y-3 rounded-xl border border-[#BFDBFE] bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary" aria-hidden>
          <c.Icon className="h-6 w-6" />
        </span>
        <div className="flex-1 space-y-0.5">
          <p className="text-base font-bold text-foreground">
            {titleOverride ?? c.title}
          </p>
          <p className="text-xs text-muted-foreground">{c.subtitle}</p>
        </div>
        {dismissibleId && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fermer cette suggestion"
            className="rounded p-1 text-muted-foreground hover:bg-background/60 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <ul className="space-y-1 pl-1 text-xs text-foreground/80">
        {c.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2">
            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-primary" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <a
        href={aff.url}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer sponsored"
        aria-label={c.aria}
        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-dark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50"
      >
        {c.cta}
        <ExternalLink className="h-3.5 w-3.5" />
      </a>

      <p className="flex items-start gap-1 text-[11px] leading-snug text-muted-foreground">
        <LinkIcon className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
        <span>Lien partenaire — RentaLMNP peut percevoir une commission sans surcoût pour vous</span>
      </p>
    </div>
  );
}
